import { createContext, useContext } from 'react'

import { cn } from '#/shared/lib/utils'

/**
 * The side panel's own presentation, which is not the card's.
 *
 * The product draws record surfaces two ways and the difference is deliberate:
 *
 *  - a dialog or a card on a page uses `EditableInfoCard` — grey title strip,
 *    a fixed label column, rules between rows. That reads as a form, which is
 *    what it is: those surfaces are edited in place.
 *  - the side panel uses `SidebarSection` — a small label above its content,
 *    generous gaps, no chrome at all. That reads as a record, which is what a
 *    panel is for: you opened it to look, not to fill it in.
 *
 * The console had been using the card shape in the panel, so nine facts filled
 * a screen in `min-h-12` form rows and the panel was a wall of uppercase labels
 * in a narrow column. These are the panel shapes, matching
 * `thecargo-frontend/src/widgets/shipment-sidebar/ui/sidebar-section.tsx`.
 */

const DetailSurfaceContext = createContext(false)

export const DetailSurfaceProvider = DetailSurfaceContext.Provider

/** True inside the detail panel; false in a dialog or on a record page. */
export function useInDetailPanel(): boolean {
  return useContext(DetailSurfaceContext)
}

/** A labelled block: the label above, the content beneath it. */
export function DetailSection({
  label,
  action,
  children,
  className,
}: {
  label: string
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn('flex flex-col gap-2', className)}>
      <div className="flex min-h-6 flex-wrap items-center justify-between gap-x-2.5 gap-y-1">
        <span className="text-xs leading-none text-gray-500 uppercase select-none">
          {label}
        </span>
        {action}
      </div>
      {children}
    </section>
  )
}

export interface DetailFact {
  label: string
  value: React.ReactNode
  /** Give the fact the full row — for a long note, an address, a list. */
  wide?: boolean
}

/**
 * The facts themselves: label above value, flowing into as many columns as the
 * panel is wide enough for.
 *
 * Two columns at the panel's normal width and three when it is widened, so
 * widening buys more record on screen rather than more whitespace. A fact whose
 * value is a sentence takes the whole row — halving a sentence to fit a grid
 * makes it harder to read, not easier.
 */
export function DetailFacts({ facts }: { facts: DetailFact[] }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4 detail-facts">
      {facts.map((fact) => (
        <div
          key={fact.label}
          className={cn('flex min-w-0 flex-col gap-1', fact.wide && 'col-span-full')}
        >
          <span className="text-2xs leading-none font-medium tracking-wide text-gray-400 uppercase select-none">
            {fact.label}
          </span>
          <span className="min-w-0 text-sm break-words text-gray-900">
            {fact.value === null || fact.value === undefined || fact.value === '' ? (
              <span className="text-gray-300">—</span>
            ) : (
              fact.value
            )}
          </span>
        </div>
      ))}
    </div>
  )
}
